import { useState, useMemo } from "react";
import { useScheduleEditor } from "../ScheduleEditorProvider";
import type { Day, DayOfWeek, Schedule } from "../types";
import { HOUR_MARKERS, COLORS } from "../constants";
import { calculateOverlapLayout, formatDayDate, getDateForDay, extractDatePart } from "../utils";
import ScheduleBlock from "./ScheduleBlock";

type Props = {
  day: Day;
  dayIndex: number;
  isDragging: (scheduleId: string) => boolean;
  onStartResize: (scheduleId: string, edge: "left" | "right") => void;
  onStartDrag: (scheduleId: string, clickX: number) => void;
};

// Check if a schedule should be visible for a given week
// Check if a schedule should be visible on a specific day column
const isScheduleVisibleOnDay = (schedule: Schedule, dayKey: DayOfWeek, dayDate: string, weekStart: Date): boolean => {
  switch (schedule.type) {
    case "recurring":
      return schedule.daysOfWeek.includes(dayKey);

    case "date-range": {
      if (!schedule.startDate || !schedule.endDate) return schedule.daysOfWeek.includes(dayKey);
      // Must be within the date range AND on an active day of week (compare date parts only)
      const startDate = extractDatePart(schedule.startDate);
      const endDate = extractDatePart(schedule.endDate);
      if (dayDate < startDate || dayDate > endDate) return false;
      return schedule.daysOfWeek.length === 0 || schedule.daysOfWeek.includes(dayKey);
    }

    case "one-time": {
      if (!schedule.startDate) return schedule.daysOfWeek.includes(dayKey);
      // Compare date parts only (startDate may be a datetime)
      const eventDate = extractDatePart(schedule.startDate);
      // Show on all days between start and end if endDate is set
      if (schedule.endDate) {
        const endDate = extractDatePart(schedule.endDate);
        return dayDate >= eventDate && dayDate <= endDate;
      }
      return dayDate === eventDate;
    }

    default:
      return schedule.daysOfWeek.includes(dayKey);
  }
};

export default ({ day, dayIndex, isDragging, onStartResize, onStartDrag }: Props) => {
  const {
    schedules,
    selectedId,
    selectSchedule,
    createScheduleFromSlideshow,
    getSlideshow,
    weekStart,
    canEditSchedule,
  } = useScheduleEditor();
  const [isDropTarget, setIsDropTarget] = useState(false);

  const dayDate = getDateForDay(weekStart, dayIndex);

  const daySchedules = useMemo(
    () => schedules.filter((s) => isScheduleVisibleOnDay(s, day.key, dayDate, weekStart)),
    [schedules, day.key, dayDate, weekStart],
  );

  // Calculate overlap layout for stacked positioning
  const overlapLayout = useMemo(() => calculateOverlapLayout(daySchedules), [daySchedules]);

  // Get max rows needed for this day
  const maxRows = useMemo(() => {
    let max = 1;
    for (const layout of overlapLayout.values()) {
      if (layout.totalColumns > max) max = layout.totalColumns;
    }
    return max;
  }, [overlapLayout]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(true);
  };

  const handleDragLeave = () => {
    setIsDropTarget(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);

    const slideshowId = e.dataTransfer.getData("slideshowId");
    if (slideshowId) {
      createScheduleFromSlideshow(slideshowId, day.key);
    }
  };

  return (
    <div
      className="flex"
      style={{
        height: maxRows === 1 ? 80 : maxRows * 52 + 4,
        borderBottom: `1px solid ${COLORS.white10}`,
      }}
    >
      {/* Day label */}
      <div
        className="w-20 flex-shrink-0 flex flex-col items-center justify-center"
        style={{ backgroundColor: COLORS.navy, borderRight: `1px solid ${COLORS.white10}` }}
      >
        <span className="text-sm font-medium" style={{ color: COLORS.white70 }}>
          {day.short}
        </span>
        <span className="text-xs" style={{ color: COLORS.white50 }}>
          {formatDayDate(weekStart, dayIndex)}
        </span>
      </div>

      {/* Schedule row body */}
      <div
        className="flex-1 relative transition-colors"
        style={{ backgroundColor: isDropTarget ? `${COLORS.orange}15` : COLORS.navyLight }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Hour grid lines (vertical) */}
        {HOUR_MARKERS.map((h) => (
          <div
            key={h}
            className="absolute top-0 bottom-0"
            style={{ left: `${(h / 24) * 100}%`, borderLeft: `1px solid ${COLORS.white10}` }}
          />
        ))}

        {/* Schedule blocks */}
        {daySchedules.map((schedule) => {
          const layout = overlapLayout.get(schedule.id);
          return (
            <ScheduleBlock
              key={schedule.id}
              schedule={schedule}
              slideshow={getSlideshow(schedule.slideshowId)}
              isSelected={selectedId === schedule.id}
              isDragging={isDragging(schedule.id)}
              isEditable={canEditSchedule(schedule)}
              overlapRow={layout?.column ?? 0}
              overlapTotalRows={layout?.totalColumns ?? 1}
              onSelect={() => selectSchedule(schedule.id)}
              onStartResize={(edge: "left" | "right") => onStartResize(schedule.id, edge)}
              onStartDrag={(clickX: number) => onStartDrag(schedule.id, clickX)}
            />
          );
        })}

        {/* Drop indicator */}
        {isDropTarget && (
          <div
            className="absolute inset-2 border-2 border-dashed rounded-lg pointer-events-none"
            style={{ borderColor: COLORS.orange }}
          />
        )}
      </div>
    </div>
  );
};
